import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Plus, Trash2, DollarSign } from "lucide-react";

export interface PaymentMilestone {
  id: string;
  name: string;
  percentage: number;
}

export interface PaymentStructure {
  totalAmount: number;
  currency: string;
  milestones: PaymentMilestone[];
}

interface SimplePaymentStructureProps {
  totalAmount: number;
  onPaymentStructureChange: (paymentStructure: PaymentStructure) => void;
  initialMilestones?: PaymentMilestone[];
  currency?: string;
}

const SimplePaymentStructure: React.FC<SimplePaymentStructureProps> = ({
  totalAmount,
  onPaymentStructureChange,
  initialMilestones,
  currency = "INR",
}) => {
  const [milestones, setMilestones] = useState<PaymentMilestone[]>(
    initialMilestones || [
      { id: "1", name: "Advance Payment", percentage: 30 },
      { id: "2", name: "Milestone Payment", percentage: 40 },
      { id: "3", name: "Final Payment", percentage: 30 },
    ]
  );

  // Calculate total percentage
  const totalPercentage = milestones.reduce(
    (sum, milestone) => sum + milestone.percentage,
    0
  );

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate amount for each milestone
  const calculateAmount = (percentage: number) => {
    return (totalAmount * percentage) / 100;
  };

  // Add new milestone
  const addMilestone = () => {
    const newMilestone: PaymentMilestone = {
      id: Date.now().toString(),
      name: `Milestone ${milestones.length + 1}`,
      percentage: 0,
    };
    setMilestones([...milestones, newMilestone]);
  };

  // Remove milestone
  const removeMilestone = (id: string) => {
    if (milestones.length > 1) {
      setMilestones(milestones.filter((milestone) => milestone.id !== id));
    }
  };

  // Update milestone
  const updateMilestone = (
    id: string,
    field: keyof PaymentMilestone,
    value: string | number
  ) => {
    setMilestones(
      milestones.map((milestone) =>
        milestone.id === id ? { ...milestone, [field]: value } : milestone
      )
    );
  };

  // Auto distribute equally
  const distributeEqually = () => {
    const equalPercentage = Math.floor(100 / milestones.length);
    const remainder = 100 - equalPercentage * milestones.length;

    setMilestones(
      milestones.map((milestone, index) => ({
        ...milestone,
        percentage: equalPercentage + (index < remainder ? 1 : 0),
      }))
    );
  };

  // Effect to emit changes to parent
  useEffect(() => {
    const paymentStructure: PaymentStructure = {
      totalAmount,
      currency,
      milestones,
    };
    onPaymentStructureChange(paymentStructure);
  }, [milestones, totalAmount, currency]);

  const isValid = totalPercentage === 100;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">Payment Structure</h3>
            <span
              className={`text-sm px-2 py-1 rounded ${
                isValid
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {totalPercentage}% / 100%
            </span>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={distributeEqually}
        >
          Distribute Equally
        </Button>
      </div>

      {/* Milestones Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
          <div className="col-span-5">Milestone Name</div>
          <div className="col-span-2">Percentage</div>
          <div className="col-span-4">Amount</div>
          <div className="col-span-1">Action</div>
        </div>

        {milestones.map((milestone, index) => (
          <div
            key={milestone.id}
            className="px-4 py-3 grid grid-cols-12 gap-4 items-center border-t"
          >
            <div className="col-span-5">
              <Input
                value={milestone.name}
                onChange={(e) =>
                  updateMilestone(milestone.id, "name", e.target.value)
                }
                placeholder="Milestone name"
                className="h-9"
              />
            </div>

            <div className="col-span-2">
              <div className="relative">
                <Input
                  type="number"
                  value={milestone.percentage}
                  onChange={(e) =>
                    updateMilestone(
                      milestone.id,
                      "percentage",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  placeholder="0"
                  min="0"
                  max="100"
                  step="1"
                  className="h-9 pr-8"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                  %
                </span>
              </div>
            </div>

            <div className="col-span-4">
              <div className="text-sm font-medium">
                {formatCurrency(calculateAmount(milestone.percentage))}
              </div>
            </div>

            <div className="col-span-1">
              {milestones.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeMilestone(milestone.id)}
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Milestone Button */}
      <Button
        type="button"
        variant="outline"
        onClick={addMilestone}
        className="w-full"
        disabled={milestones.length >= 10}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add Milestone
      </Button>

      {/* Validation Message */}
      {!isValid && (
        <p className="text-sm text-red-600 text-center">
          Total percentage must equal 100%. Current total: {totalPercentage}%
        </p>
      )}
    </div>
  );
};

export default SimplePaymentStructure;
